"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";

import { Button } from "@/components/ui/button";

import { cn } from "@/lib/utils";

import { useDialogStore } from "@/stores/dialog-store";

const INFO_SECTIONS = [
  ["넥서스에이아이(주)", "대표: 이재원", "사업자 등록번호: 601-88-02866"],
  ["통신판매업신고번호: 제2025-서울서초-0439호", "주소: 서울특별시 서초구 효령로 17 청진빌딩 6층"],
  ["고객문의: resizead@nexusai.kr", "1600-5117 (평일 09:00~18:00)"],
];

export default function LoginLayout({ children }: { children: React.ReactNode }) {
  const { openDialog } = useDialogStore();
  const pathname = usePathname();

  if (pathname === "/sign-in") {
    return <div className="fixed inset-0 overflow-hidden">{children}</div>;
  }

  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden bg-[linear-gradient(102.5deg,#6A4CE9_0%,#4AC8E8_96.59%)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-y-0 top-[50%] left-[-30px] mt-[-395px] h-[624px] w-[624px] -translate-x-1/2 bg-[url('/image/watermark.svg')] bg-cover bg-center bg-no-repeat opacity-10" />
        <div className="absolute inset-y-0 top-[50%] right-[-30px] mt-[-395px] h-[624px] w-[624px] translate-x-1/2 bg-[url('/image/watermark.svg')] bg-cover bg-center bg-no-repeat opacity-10" />
      </div>
      <div className="bg-background z-10 mx-6 flex h-full flex-col items-center overflow-auto p-12">
        <div className={cn("bg-background flex w-full max-w-[460px] flex-1 items-center")}>
          <div className="w-full">
            <header className={cn("flex w-full flex-col items-center gap-5")}>
              <h1 className="sr-only">ResizeAd</h1>
              <Image width={193} height={36} src="/assets/logo.svg" alt="" aria-hidden="true" />
              <p className="text-base font-semibold text-neutral-600">
                업로드 한 번으로, 필요한 모든 사이즈 다운로드
              </p>
            </header>
            <main className="w-full py-[40px]">{children}</main>
          </div>
        </div>
        <footer className="flex flex-col items-center p-6">
          <Image width={137} height={28} src="/assets/nexusai_logo.svg" alt="" aria-hidden="true" />
          <p className="my-4 text-xs text-neutral-500">
            본 서비스는{" "}
            <strong className="text-primary-500 font-semibold">
              <a href="https://www.nexusai.kr/" target="_blank">
                넥서스에이아이(주)
              </a>
            </strong>
            가 제공 및 운영합니다.
          </p>

          {/* 회사 정보 섹션 */}
          <div className="flex flex-col items-center gap-1">
            {INFO_SECTIONS.map((section, i) => (
              <ul key={i} className="flex flex-wrap justify-center gap-2">
                {section.map((text, idx) => (
                  <li
                    key={idx}
                    className={cn(
                      "relative text-xs text-neutral-500",
                      !(idx === section.length - 1) &&
                        "mr-2 before:absolute before:top-1/2 before:-right-2 before:h-3 before:w-[1px] before:-translate-y-1/2 before:bg-neutral-300"
                    )}
                  >
                    {text}
                  </li>
                ))}
              </ul>
            ))}
          </div>

          {/* 내비게이션 링크 */}
          <nav
            aria-label="푸터 내비게이션"
            className="mb-2 flex flex-col gap-2 text-xs text-neutral-500"
          >
            <ul className="mt-4 flex flex-wrap items-center gap-4 font-semibold">
              <li>
                <Button variant="ghost" size="xs" onClick={() => openDialog("service-terms")}>
                  이용 약관
                </Button>
              </li>
              <hr className="h-2.5 border-l border-neutral-300" />
              <li>
                <Button variant="ghost" size="xs" onClick={() => openDialog("privacy-policy")}>
                  개인정보 처리방침
                </Button>
              </li>
            </ul>
          </nav>
          <small className="text-xs text-neutral-500">
            &#169; 2025 Nexus AI corp. All rights reserved.
          </small>
        </footer>
      </div>
    </div>
  );
}
